import * as THREE from 'three';

/**
 * Shared shaft geometry: subdivided box for smoother lighting and vertical UV bands
 * (window rows read clearly when scaled tall).
 */
export function createBuildingShaftGeometry(): THREE.BoxGeometry {
  return createBuildingShaftGeometryWithSegments(40);
}

/** Vertical segments control window-band resolution on the emissive UV grid. */
export function createBuildingShaftGeometryWithSegments(segY: number): THREE.BoxGeometry {
  const geo = new THREE.BoxGeometry(1, 1, 1, 2, segY, 2);
  const uv = geo.attributes.uv;
  geo.setAttribute('uv2', uv.clone());
  return geo;
}

let cachedFacadeEmissive: THREE.CanvasTexture | null = null;

/**
 * High-density urban curtain-wall emissive: retail base, mullions, spandrels,
 * cooling tower band, crown darkness — reads “real tower” more than a flat grid.
 */
export function getFacadeEmissiveTexture(): THREE.CanvasTexture {
  if (cachedFacadeEmissive) return cachedFacadeEmissive;

  const cw = 896;
  const ch = 2560;
  const canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d')!;

  ctx.fillStyle = '#030508';
  ctx.fillRect(0, 0, cw, ch);

  const cols = 6;
  const rows = 48;
  const cellW = cw / cols;
  const cellH = ch / rows;

  const retailRows = Math.max(2, Math.floor(rows * 0.09));
  const crownRows = Math.max(3, Math.floor(rows * 0.11));

  /** Dark spandrel bands between floor groups */
  const spandrelEvery = 4;

  for (let row = 0; row < rows; row++) {
    const yTop = row * cellH;
    const isRetail = row < retailRows;
    const isCrown = row >= rows - crownRows;
    const spandrel = !isRetail && !isCrown && row % spandrelEvery === spandrelEvery - 1;

    for (let col = 0; col < cols; col++) {
      const x = col * cellW;
      const y = yTop;

      let winW = cellW * (isRetail ? 0.72 : 0.5);
      let winH = cellH * (isRetail ? 0.68 : 0.58);
      if (isCrown) {
        winW *= 0.72;
        winH *= 0.65;
      }
      const px = x + (cellW - winW) / 2;
      const py = y + (cellH - winH) / 2;

      if (spandrel) {
        ctx.fillStyle = 'rgba(8,10,14,0.92)';
        ctx.fillRect(x + 1, y + 1, cellW - 2, cellH - 2);
        continue;
      }

      let litChance = isRetail ? 0.82 : isCrown ? 0.22 : 0.58;
      if (isCrown) litChance *= 0.65;
      const isLit = Math.random() < litChance;

      if (isLit) {
        const warmth = Math.random();
        const r = Math.floor(255 * (0.68 + warmth * 0.32));
        const g = Math.floor(168 + warmth * 95);
        const b = Math.floor(88 + (1 - warmth) * 110);
        ctx.fillStyle = `rgb(${r},${g},${b})`;
        ctx.fillRect(px + 1, py + 1, winW - 2, winH - 2);
        if (!isRetail && !isCrown && Math.random() > 0.72) {
          ctx.fillStyle = `rgba(200,230,255,0.14)`;
          ctx.fillRect(px + winW * 0.38, py + winH * 0.15, winW * 0.22, winH * 0.55);
        }
      } else {
        ctx.fillStyle = 'rgba(12,14,20,0.94)';
        ctx.fillRect(px + 1, py + 1, winW - 2, winH - 2);
      }
    }
  }

  /** Vertical mullions */
  ctx.strokeStyle = 'rgba(0,0,0,0.55)';
  ctx.lineWidth = 1.5;
  for (let col = 1; col < cols; col++) {
    const mx = col * cellW;
    ctx.beginPath();
    ctx.moveTo(mx, retailRows * cellH);
    ctx.lineTo(mx, ch - crownRows * cellH);
    ctx.stroke();
  }
  /** Horizontal floor lines (subtle) */
  ctx.strokeStyle = 'rgba(0,0,0,0.22)';
  ctx.lineWidth = 1;
  for (let row = 1; row < rows; row++) {
    if (row <= retailRows || row >= rows - crownRows) continue;
    const my = row * cellH;
    ctx.beginPath();
    ctx.moveTo(2, my);
    ctx.lineTo(cw - 2, my);
    ctx.stroke();
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 16;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  cachedFacadeEmissive = tex;
  return tex;
}
